import { flatten } from 'lodash';
import Lang from '/@/modules/core/app/services/Lang';

export class FormSelectConfig {
  /**
   * @var {array}
   */
  options;

  /**
   * @var {boolean}
   */
  searchable;

  /**
   * @var {string}
   */
  trackBy;

  /**
   * @var {string}
   */
  placeholder;

  /**
   * @var {string}
   */
  label;

  /**
   * @var {boolean}
   */
  multiple;

  /**
   * @var {boolean}
   */
  preselectFirst;

  /**
   * @var {string}
   */
  groupValues;

  /**
   * @var {string}
   */
  groupLabel;

  /**
   * @var {Boolean}
   */
  groupSelect;

  /**
   * @var {string}
   */
  selectLabel;

  /**
   * @var {string}
   */
  selectGroupLabel;

  /**
   * @var {string}
   */
  noOptionsLabel;

  /**
   * @var {string}
   */
  noResultLabel;

  /**
   * @var {Function}
   */
  customLabel;

  /**
   * @var {boolean}
   */
  showLabels;

  /**
   * @var {boolean}
   */
  allowEmpty;

  /**
   * @var {array}
   */
  values;

  /**
   * @var {Array}
   */
  hiddenValues;

  /**
   * @var {Array}
   */
  disabledValues;

  /**
   * @var {Boolean}
   */
  isConfirmationRequired;

  /**
   * @var {Object}
   */
  confirmationModalFactory;

  /**
   * @var {Boolean}
   */
  closeOnSelect;

  /**
   * @var {Number}
   */
  limit;

  /**
   * @var {Function}
   */
  limitText;

  /**
   * @var {Boolean}
   */
  internalSearch;

  /**
   * @var {Number}
   * in milliseconds
   */
  debounceTime;

  /**
   * @var {Boolean}
   */
  preserveSearch;

  /**
   * @var {Boolean}
   */
  isAsync;

  /**
   * @var {Number}
   */
  search;

  /**
   * @var {Number}
   */
  page;

  /**
   * @var {Boolean}
   */
  clearOnSelect;

  preserveSelectedOptions;

  constructor(config = {}) {
    this.options = config.options ?? [];
    this.searchable = config.searchable ?? false;
    this.placeholder = config.placeholder ?? Lang.t('core.b9aa31ff-1749-49ec-989d-84f671b06f5b');
    this.trackBy = config.trackBy ?? 'key';
    this.label = config.label ?? 'value';
    this.multiple = config.multiple ?? false;
    this.preserveSelectedOptions = config.preserveSelectedOptions ?? false;
    this.preselectFirst = config.preselectFirst ?? false;
    this.customLabel = config.customLabel ?? undefined;
    this.groupLabel = config.groupLabel ?? 'name';
    this.groupValues = config.groupValues ?? null;
    this.groupSelect = config.groupSelect ?? false;
    this.selectLabel = config.selectLabel || '';
    this.selectGroupLabel = config.selectGroupLabel || '';
    this.noOptionsLabel = config.noOptionsLabel ?? Lang.t('core.list-is-empty');
    this.noResultLabel = config.noResultLabel ?? Lang.t('core.no-elements-found');
    this.showLabels = config.showLabels ?? true;
    this.allowEmpty = config.allowEmpty || false;
    this.hiddenValues = config.hiddenValues ?? [];
    this.disabledValues = config.disabledValues ?? [];
    this.isConfirmationRequired = !!config.confirmationModalFactory;
    this.confirmationModalFactory = config.confirmationModalFactory;
    this.closeOnSelect = config.closeOnSelect ?? true;
    this.clearOnSelect = config.clearOnSelect ?? true;
    this.limit = config.limit ?? 9999;
    this.limitText = config.limitText ?? ((count) => Lang.t('core.limit-text', { count }));
    this.internalSearch = config.internalSearch === undefined ? true : config.internalSearch;
    this.debounceTime = config.debounceTime ?? 300;
    this.preserveSearch = config.preserveSearch ?? false;
    this.isAsync = config.isAsync ?? false;

    this.asyncLimit = config.asyncLimit ?? 50;
    this.search = config.search ?? '';
    this.page = config.page ?? 0;
  }

  setAsync() {
    this.isAsync = true;
    this.closeOnSelect = false;
    this.internalSearch = false;
    this.preserveSearch = true;
  }

  resetSearch(value = '') {
    this.search = value;
    this.page = 0;
  }

  shouldTriggerSearch(search) {
    return this.isAsync && (search.length >= 3 || search === '');
  }

  incrementPage() {
    this.page += 1;
  }

  /**
   * @returns {boolean}
   */
  get hasGrouping() {
    return !!(this.groupLabel && this.groupValues);
  }

  /**
   * @param params
   * @returns {Array|Promise<any[]>}
   */
  // eslint-disable-next-line no-unused-vars
  getOptions(params = {}) {
    let filteredValues = this.filterVisibleOptions(this.options);
    return this.getDisableOptions(filteredValues);
  }

  /**
   * @param options
   * @param groupKey
   * @returns {*}
   */
  // eslint-disable-next-line no-unused-vars
  getGroupedOptions(options, groupKey) {
    return options;
  }

  /**
   * @param options
   * @returns {*}
   */
  filterVisibleOptions(options) {
    return options.filter(
      (option) =>
        this.hiddenValues.findIndex((hiddenValue) => hiddenValue === option[this.trackBy]) === -1
    );
  }

  /**
   * @param options
   * @returns {*}
   */
  getDisableOptions(options) {
    return options.map((option) => {
      if (this.disabledValues.find((disabledValue) => disabledValue === option[this.trackBy])) {
        return { ...option, $isDisabled: true };
      }
      return option;
    });
  }

  /**
   * @param options
   * @returns {Array}
   */
  getFlattenedGroupedOptions(options) {
    return flatten(options.map((option) => option[this.groupValues]));
  }

  setHiddenValues(hiddenValues) {
    this.hiddenValues = hiddenValues;
  }
}
